import React, { useState } from "react";
import {
  Drawer,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  useTheme,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useSnackbar } from "notistack";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  AddPlayListBoxStyles,
  CancleIconButton,
  PlayListCoverPicStyles,
  PositionTopRight,
} from "./css";
import {
  createPlaylist,
  sendPutRequest,
  uploadVideoThumbnail,
} from "../../services/ContentServices";
import { PlaylistData } from "./types";

interface AddPlaylistDrawerProps {
  open: boolean;
  onClose: () => void;
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>;
}

const AddPlaylistDrawer: React.FC<AddPlaylistDrawerProps> = ({
  open,
  onClose,
  setSelectedTab,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [playlistData, setPlaylistData] = useState<PlaylistData>({
    name: "",
    coverPicture: null,
    coverPicturePreview: null,
    description: "",
    availableForVisitors: false,
  });

  const inputStyle = {
    marginTop: theme.spacing(1),
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      fontSize: "1rem",
    },
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const newValue: string | boolean = type === "checkbox" ? checked : value;
    setPlaylistData((prevPlaylistData) => ({
      ...prevPlaylistData,
      [name]: newValue,
    }));
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPlaylistData((prevPlaylistData) => ({
          ...prevPlaylistData,
          coverPicture: file,
          coverPicturePreview: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveCoverPicture = () => {
    setPlaylistData((prevPlaylistData) => ({
      ...prevPlaylistData,
      coverPicture: null,
      coverPicturePreview: null,
    }));
  };

  const handleSavePlaylist = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (!playlistData.name) {
        return enqueueSnackbar("Please fill  title");
      }
      if (!playlistData.coverPicture) {
        return enqueueSnackbar("Thumbnail is required");
      }
      const signUrlResponse = await uploadVideoThumbnail(
        playlistData.coverPicture.name
      );
      const signUrl = signUrlResponse.data?.data as string;
      const thumbAwsUrl = signUrl.split("?")[0];
      const upload = await sendPutRequest({
        signUrl,
        file: playlistData.coverPicture,
      });
      if (upload.status == 200) {
        const body = {
          name: playlistData.name as string,
          description: playlistData.description as string,
          thumbnailUrl: thumbAwsUrl as string,
          availableForVisitors: playlistData.availableForVisitors as boolean,
        };
        const response = await createPlaylist(body);
        if (response == 201) {
          enqueueSnackbar("Playlist created successfully");
          setSelectedTab(1);
          onClose();
          setSubmitting(false);
          handleResetForm();
        } else {
          enqueueSnackbar("Failed to create playlist", { variant: "error" });
        }
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Failed to create playlist", { variant: "error" });
    }
  };

  const handleResetForm = () => {
    setPlaylistData({
      name: "",
      coverPicture: null,
      coverPicturePreview: null,
      description: "",
      availableForVisitors: false,
    });
    setSubmitting(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: theme.palette.background.paper,
          borderTopLeftRadius: theme.spacing(3),
          borderBottomLeftRadius: theme.spacing(3),
          width: 350,
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSavePlaylist}
        aria-label="Playlist Form"
        sx={{
          backgroundColor: theme.palette.background.paper,
          height: "100vh",
          padding: theme.spacing(3),
          boxSizing: "border-box",
        }}
      >
        <Box mb={2} mt={2} sx={AddPlayListBoxStyles}>
          <IconButton onClick={onClose} sx={{ marginRight: theme.spacing(1) }}>
            <KeyboardBackspaceIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            Add New Playlist
          </Typography>
        </Box>

        <Divider sx={{ marginBottom: theme.spacing(2) }} />
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <label htmlFor="name">
            Playlist Name{" "}
            <span style={{ color: theme.palette.text.secondary }}>*</span>
          </label>
          <TextField
            id="name"
            variant="outlined"
            name="name"
            value={playlistData.name}
            onChange={handleInputChange}
            sx={inputStyle}
            required
            fullWidth
          />
        </Box>
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <Typography sx={{ marginBottom: 2 }}>
            Upload Thumbnail{" "}
            <span style={{ color: theme.palette.text.secondary }}>*</span>
          </Typography>
          {playlistData.coverPicturePreview ? (
            <Box sx={PlayListCoverPicStyles}>
              <img
                src={playlistData.coverPicturePreview}
                width={150}
                height={100}
                style={{
                  objectFit: "cover",
                }}
              />
              <IconButton
                sx={PositionTopRight}
                onClick={handleRemoveCoverPicture}
              >
                <CancelIcon sx={CancleIconButton} />
              </IconButton>
            </Box>
          ) : (
            <>
              <input
                type="file"
                accept="image/jpeg, image/jpg, image/png"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
                id="thumbnail-upload"
              />
              <label htmlFor="thumbnail-upload">
                <Box
                  component="div"
                  sx={{
                    border: `1px dashed ${theme.palette.primary.main}`,
                    borderRadius: theme.shape.borderRadius,
                    padding: theme.spacing(2),
                    marginTop: theme.spacing(1),
                    cursor: "pointer",
                  }}
                >
                  <Typography sx={{ color: theme.palette.text.disabled }}>
                    Choose a file
                  </Typography>
                </Box>
              </label>
            </>
          )}
        </Box>
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <label htmlFor="description">Description</label>
          <TextField
            id="description"
            variant="outlined"
            name="description"
            value={playlistData.description}
            onChange={handleInputChange}
            sx={inputStyle}
            multiline
            rows={12}
            fullWidth
          />
        </Box>
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={playlistData.availableForVisitors}
                onChange={handleInputChange}
                name="availableForVisitors"
                color="primary"
              />
            }
            label="Available for Visitors"
          />
        </Box>
        <Box sx={{ marginTop: theme.spacing(4), textAlign: "center" }}>
          <Button
            disabled={submitting}
            type="submit"
            variant="outlined"
            color="primary"
            sx={{ fontSize: "1rem", borderRadius: theme.spacing(1) }}
          >
            Add Playlist
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleResetForm}
            sx={{
              fontSize: "1rem",
              borderRadius: theme.spacing(1),
              marginLeft: theme.spacing(2),
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddPlaylistDrawer;
